import { FlowjoCommons } from '@portal/portal-core';

const { Net } = FlowjoCommons;

interface SuccessesAndFailures {
  successes: unknown[];
  failures: Record<string, string>[];
}

export const convertToSuccessesAndFailures = (
  obj?: Record<string, unknown> | null,
): SuccessesAndFailures => {
  // Preserve original behavior: falsy input returns an empty object.
  if (!obj) return {} as SuccessesAndFailures;
  const successes: unknown[] = [];
  const failures: Record<string, string>[] = [];
  const returnObj = {} as SuccessesAndFailures;
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object') {
      successes.push(obj[key]);
    } else if (typeof obj[key] === 'string') {
      const userObj: Record<string, string> = {};
      userObj[key] = obj[key] as string;
      failures.push(userObj);
    }
  });
  returnObj.successes = successes;
  returnObj.failures = failures;
  return returnObj;
};

// Wrapped `succeeded`/`failed` interface returned to callers.
export interface WrappedHttpResult {
  succeeded?: boolean;
  failed?: boolean;
  error?: unknown;
  errorDetail?: unknown;
  data?: unknown;
}

// Shape of the resolved value from `Net.sendJson` (typed as `unknown` upstream).
interface HttpResponse {
  code: number;
  data: string;
}

// Wraps HTTP request in safe `succeeded`/`failed` interface
export const wrapHttpRequest = (promise: Promise<unknown>): Promise<WrappedHttpResult> =>
  promise
    .then((response) => {
      const { code, data } = response as HttpResponse;
      return code === Net.reponses.OK
        ? { succeeded: true, data: JSON.parse(data).result }
        : { failed: true, error: code, errorDetail: JSON.parse(data).error };
    })
    .catch((err: unknown) => ({ failed: true, error: err }));

interface AdminClientConfig {
  // Optional at this boundary: consumer saga configs thread `getAuthToken` in at
  // runtime but type it optionally (some carry no static shape for it).
  getAuthToken?: () => string | null | undefined;
}

export const sendAction = (
  config: AdminClientConfig,
  // Accept any object action (consumer sagas pass typed action interfaces, which
  // are not assignable to `Record<string, unknown>`). Forwarded untyped to Net.
  action: object,
  url?: string,
  timeout?: number,
): Promise<WrappedHttpResult> => {
  // Non-null assertion preserves the original runtime call (throws if absent);
  // `getAuthToken` is optional only at the type boundary.
  const authToken = config.getAuthToken!();
  if (authToken) {
    const headers = { Authorization: `Bearer ${authToken}` };
    const rejectUnauthorized = undefined;
    return wrapHttpRequest(Net.sendJson(url, action, headers, rejectUnauthorized, timeout));
  }
  return wrapHttpRequest(Net.sendJson(url, action));
};
